import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { useDataProvider, useNotify, usePermissions, useQuery } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import ModalWithButton from '@src/components/Common/ModalWithButton';
import { spinnerElement } from '@src/components/Common/utils';
import ServiceJson from './ServiceJson';

/**
 * The service's config, version by version — laid out the way deployed items
 * are: the version picker on a toolbar, the body under it, and the control
 * that writes a new one beneath. A config and a deployed item are the same
 * kind of thing to read, so they are the same thing to look at.
 */
const ServiceConfigsTab = ({ record, canEdit }: { record: any; canEdit: boolean }) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();

    /**
     * Which version is on screen, or null for "the newest, whichever that is".
     *
     * Null rather than a number on purpose: a write makes a new newest, and a
     * stored number would still be pointing at the one that was newest when it
     * was stored. Deriving it means the panel follows the new version in with
     * nothing to keep in step.
     */
    const [selected, setSelected] = useState<number | null>(null);

    const { data, loaded, refetch } = useQuery({
        type: 'getList',
        resource: 'serviceowner_service_configs',
        payload: {
            pagination: { page: 1, perPage: 20 },
            sort: { field: 'version', order: 'desc' },
            filter: { service_id: record.id },
        },
    });

    /**
     * Newest first. The endpoint is asked for that order, but the ordering is
     * not part of its contract — and "which one is in force" has to be right,
     * so it is decided here rather than assumed.
     */
    const configs: any[] = useMemo(
        () => [...((data as any[]) || [])].sort((a, b) => (b.version ?? 0) - (a.version ?? 0)),
        [data],
    );

    const current =
        (selected !== null && configs.find((config) => config.version === selected)) || configs[0];

    /** the modal hands back the edited text, or null when it was cleared */
    const save = (next: any) => {
        if (next == null || (typeof next === 'string' && !next.trim())) {
            notify('Empty dictionary is not accepted', 'warning');
            return;
        }
        let parsed: any;
        try {
            parsed = typeof next === 'string' ? JSON.parse(next) : next;
        } catch (e) {
            notify('Invalid JSON', 'warning');
            return;
        }
        if (!Object.keys(parsed || {}).length) {
            notify('Empty dictionary is not accepted', 'warning');
            return;
        }
        dataProvider
            .create('serviceowner_service_configs', { data: { service: record.id, config: parsed } })
            .then(() => {
                notify('Service config updated', 'success');
                // what was just written is the newest, so follow the newest —
                // which is what a null selection means
                setSelected(null);
                refetch();
            })
            .catch(() => notify('Something went wrong', 'warning'));
    };

    if (!loaded) return spinnerElement;

    /* Nothing set yet: the invitation is the whole panel rather than a button
       stranded above a line of grey text. A reader gets the sentence without
       an editor they cannot save from. */
    if (configs.length === 0) {
        return (
            <div className="svc-tab">
                <div className="svc-blank">
                    <div className="svc-blank__title">No service config yet</div>
                    <p className="svc-blank__body">
                        A service config is the settings your own tooling reads when it acts on this
                        service. {canEdit ? 'Write the first version to get started.' : ''}
                    </p>
                    {canEdit && (
                        <ModalWithButton
                            handleOk={save}
                            editorType="json"
                            buttonTitle="Add service config"
                            initialValue={'{}'}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="svc-tab">
            {/* the version picker and the editor share a line: one chooses what
                you are looking at, the other changes it */}
            <div className="svc-tab__bar">
                {/* every option already reads "Version n" */}
                <Select
                    value={current?.version}
                    onChange={setSelected}
                    options={configs.map((config) => ({
                        value: config.version,
                        label: `Version ${config.version}`,
                    }))}
                    style={{ minWidth: 170 }}
                    aria-label="Select config version"
                />

                {/* a reader has the JSON below; only an owner's admin gets the editor */}
                {canEdit && (
                    <ModalWithButton
                        handleOk={save}
                        editorType="json"
                        buttonTitle="Edit service config"
                        initialValue={JSON.stringify(current?.config ?? {}, null, 4)}
                    />
                )}
            </div>

            <ServiceJson data={current?.config ?? {}} />
        </div>
    );
};

export default ServiceConfigsTab;
